import type { AddressCandidate, AddressHistorySelectionMode } from "../../sdk/iframeAddressHistory";

// VEX-7420: selection mode controls the default decision applied to addresses
// the user never touches.
//   - "confirm_by_default" (opt-out, current prod): all addresses default to
//     `confirm`; the user deselects (removes) addresses to `deny`.
//   - "deny_by_default" (opt-in): all addresses default to `deny`; the user
//     selects the addresses they've lived at to `confirm`.
// Canonical definition lives on the SDK launcher (the public option surface);
// alias it here so a future mode added there propagates to the rule for free.
export type SelectionMode = AddressHistorySelectionMode;

export type Decision = "confirm" | "deny";

// Candidate addresses carry the original API hash + an address_uuid + display
// string. The raw hash is preserved so the decision payload sent on submit can
// include the full address object (mirrors the persons-api PATCH body shape).
export type Address = { uuid: string; display: string; raw: AddressCandidate };

export type DecisionEntry = {
  address_uuid: string;
  decision: Decision;
  address: AddressCandidate;
};

// Fail-safe: an absent/unrecognized selection mode falls back to opt-out so a
// dropped field over-confirms (current behavior) rather than silently denying.
// Lives here, beside the rule it protects, so the two are tested together.
export function coerceSelectionMode(raw: unknown): SelectionMode {
  return raw === "deny_by_default" ? "deny_by_default" : "confirm_by_default";
}

/**
 * Maps the original candidate set to confirm/deny decisions per the selection mode.
 *
 * `touchedUuids` is whichever set the active mode populated: *selected* addresses
 * in opt-in (→ confirm), *removed* addresses in opt-out (→ deny). The two are
 * distinct UI state but collapse to one set here because opt-in is checkbox-only,
 * so they never go live in the same session. If opt-in ever gains a remove flow,
 * this single-set shape becomes unsafe — split it back into two sets.
 *
 * `candidates` must be the ORIGINAL set, never the live post-removal list, or
 * removed addresses silently vanish instead of shipping as explicit `deny` (D4).
 */
export function computeAddressDecisions(input: {
  selectionMode: SelectionMode;
  candidates: Address[];
  touchedUuids: Set<string>;
}): DecisionEntry[] {
  const { selectionMode, candidates, touchedUuids } = input;
  const untouched: Decision = selectionMode === "deny_by_default" ? "deny" : "confirm";
  const touched: Decision = selectionMode === "deny_by_default" ? "confirm" : "deny";
  return candidates.map(a => ({
    address_uuid: a.uuid,
    decision: touchedUuids.has(a.uuid) ? touched : untouched,
    address: a.raw,
  }));
}
